import { datadogRum } from '@datadog/browser-rum';

import { getConfig } from 'src/global-config';

// ----------------------------------------------------------------------

export type DatadogDebugScope = {
  userId: string;
  organizationId: string;
};

export type DatadogDebugLinks = {
  session?: { id: string; url: string };
  logsUrl?: string;
};

const LOGS_LOOKBACK_MINUTES = 30;
const LOGS_LOOKAHEAD_MINUTES = 5;

// Datadog serves its UI at app.<site> on the US1 and EU1 sites, and at the bare site everywhere else.
const APP_SUBDOMAIN_SITES = ['datadoghq.com', 'datadoghq.eu'];

const minutesFrom = (time: number, minutes: number) => time + minutes * 60_000;

function datadogAppOrigin(site: string) {
  return `https://${APP_SUBDOMAIN_SITES.includes(site) ? 'app.' : ''}${site}`;
}

function trackedSession() {
  const id = datadogRum.getInternalContext()?.session_id;
  const url = datadogRum.getSessionReplayLink();

  return id && url ? { id, url } : undefined;
}

function logsQuery(sessionId: string | undefined, { userId, organizationId }: DatadogDebugScope) {
  const scopes = [
    sessionId ? `@session_id:${sessionId}` : '',
    userId ? `@Properties.usr.id:${userId}` : '',
    organizationId ? `(@Properties.org.id:${organizationId} -@Properties.usr.id:*)` : '',
  ];

  return scopes.filter(Boolean).join(' OR ');
}

function logsExplorerUrl(query: string, reportedAt: number) {
  const params = new URLSearchParams({
    query,
    from_ts: String(minutesFrom(reportedAt, -LOGS_LOOKBACK_MINUTES)),
    to_ts: String(minutesFrom(reportedAt, LOGS_LOOKAHEAD_MINUTES)),
    live: 'false',
  });

  return `${datadogAppOrigin(getConfig().datadog.site)}/logs?${params}`;
}

/**
 * Links for debugging what the user just saw: the RUM session and replay when this browser is tracked,
 * and the surrounding logs. Those cover the session, the user's own backend requests, and the
 * organization's background work, so they exist even when the browser blocks tracking.
 */
export function currentDatadogDebugLinks(scope: DatadogDebugScope): DatadogDebugLinks {
  const session = trackedSession();
  const query = logsQuery(session?.id, scope);

  return {
    session,
    logsUrl: query ? logsExplorerUrl(query, Date.now()) : undefined,
  };
}
